import { isCmoePrimaryOrchestration } from "../../config/cmoeOrchestrationMode";
import {
  isCmoeEnabled,
  isCmoeAllocationLedgerEnabled,
  isCmoeProjectionsEnabled,
  isCmoeActivityModuleEnabled,
} from "../../config/cmoeFeatureFlags";
import {
  mapRoomMatchToSummary,
  mapRegistryEntryToParticipant,
  buildCompetitionPermissionGrants,
} from "../../adapters/cmoe/roomSessionAdapter";
import { computeActiveParticipantEntryIds } from "../../domain/roomGovernance/roomGovernanceLogic";
import { buildHoldDocId } from "../../domain/cmoe/allocation/allocationTypes";
import { canAcquireExclusiveHold } from "../../domain/cmoe/allocation/allocationLedger";
import { grantLease, leaseFromManagedBy } from "../../domain/cmoe/lease/managerLease";
import { ORCHESTRATION_EVENT_TYPES } from "../../domain/cmoe/events/eventTypes";
import { firestoreMatchRepository } from "../../repositories/cmoe/firestoreMatchRepository";
import { firestoreAllocationRepository } from "../../repositories/cmoe/firestoreAllocationRepository";
import { firestoreProjectionRepository } from "../../repositories/cmoe/firestoreProjectionRepository";
import { appendMatchOrchestrationEvent } from "../../repositories/cmoe/firestoreOrchestrationEventRepository";
import { persistActivityOnGoLive } from "./activity/activityGoLiveWriter";
import { upsertCompetitionParticipant } from "../../repositories/cmoe/firestoreParticipantRepository";
import { upsertCompetitionGrant } from "../../repositories/cmoe/firestorePermissionGrantRepository";
import {
  releaseHoldsForMatch,
  acquireExclusiveHold,
} from "./allocationCoordinator";
import { applyMatchSummaryToProjections, refreshActiveHoldsProjection } from "./projectionService";
import { ensureRoomSessionCompetition, resolveCompetitionIdForRoom } from "./competitionEnsureService";
import { logCmoeSyncMismatch, logCmoeSyncError, logCmoeSyncInfo } from "./cmoeSyncLogger";
import { getRoomSessionPolicyBundle } from "../../domain/cmoe/policy/roomSessionPolicyBundle";
import { scheduleShadowValidation } from "./shadow/shadowValidationRunner";
import { recordCmoeIo, withCmoeTiming } from "./shadow/cmoePerformanceBaseline";

/**
 * @param {string} competitionId
 * @param {string} matchId
 * @param {string} eventType
 * @param {string | null} actorUid
 * @param {Record<string, unknown>} payload
 */
async function recordEvent(competitionId, matchId, eventType, actorUid, payload = {}) {
  await appendMatchOrchestrationEvent(competitionId, matchId, {
    eventId: "",
    eventType,
    eventVersion: Date.now(),
    causationId: payload.commandId || "dual-write",
    correlationId: matchId,
    actorUid,
    payload,
    occurredAtMs: Date.now(),
  });
  recordCmoeIo({ writes: 1 });
}

function shadowAfter(operation, ctx) {
  try {
    scheduleShadowValidation(operation, ctx);
  } catch {
    /* shadow tooling must never affect dual-write */
  }
}

/**
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 * @param {string[]} participantEntryIds
 */
export function checkAllocationParityWithV1(roomMatches, participantEntryIds) {
  const live = computeActiveParticipantEntryIds(roomMatches);
  const conflicts = participantEntryIds.filter((id) => live.has(id));
  return { live, conflicts };
}

/**
 * @param {string} roomCode
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null, registryEntries?: unknown[] }} roomDoc
 */
export async function syncRoomSessionGrants(roomCode, roomDoc) {
  if (!isCmoeEnabled() || !roomDoc) return;
  try {
    const competitionId = await ensureRoomSessionCompetition(roomCode);
    if (!competitionId) return;
    const grants = buildCompetitionPermissionGrants(roomDoc);
    await Promise.all(
      grants.map((g) => upsertCompetitionGrant(competitionId, g.uid, g.permissions))
    );
  } catch (err) {
    logCmoeSyncError("sync_grants_failed", err, { roomCode });
  }
}

/**
 * @param {string} roomCode
 * @param {unknown[]} registryEntries
 */
export async function syncRegistryToParticipants(roomCode, registryEntries) {
  if (!isCmoeEnabled()) return;
  try {
    const competitionId = await ensureRoomSessionCompetition(roomCode);
    if (!competitionId) return;
    for (const entry of registryEntries || []) {
      const row = mapRegistryEntryToParticipant(entry, competitionId);
      if (row) await upsertCompetitionParticipant(competitionId, row);
    }
  } catch (err) {
    logCmoeSyncError("sync_participants_failed", err, { roomCode });
  }
}

/**
 * @param {Object} params
 * @param {string} params.roomCode
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch} roomMatch
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null, registryEntries?: unknown[] } | null} roomDoc
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} [allRoomMatches]
 */
export async function dualWriteRoomMatchAfterSetup(params) {
  if (!isCmoeEnabled() || isCmoePrimaryOrchestration()) return { ok: true, skipped: true };
  return withCmoeTiming("dualWrite:Setup", async () => {
  const { roomCode, roomMatch, roomDoc, allRoomMatches = [] } = params;
  try {
    const competitionId = await ensureRoomSessionCompetition(roomCode);
    if (!competitionId) return { ok: false, code: "CMOE_DISABLED" };
    if (roomDoc?.registryEntries) {
      await syncRegistryToParticipants(roomCode, roomDoc.registryEntries);
    }
    await syncRoomSessionGrants(roomCode, roomDoc);

    const parity = checkAllocationParityWithV1(allRoomMatches, roomMatch.participantEntryIds || []);
    if (parity.conflicts.length) {
      logCmoeSyncMismatch("v1_active_participants", {
        roomCode,
        matchId: roomMatch.id,
        conflicts: parity.conflicts,
      });
    }

    const existing = await firestoreMatchRepository.getSummary(competitionId, roomMatch.id);
    const summary = {
      ...mapRoomMatchToSummary(roomMatch, competitionId),
      orchestrationVersion: (existing?.orchestrationVersion || 0) + 1,
      managerLease: leaseFromManagedBy(
        roomMatch.managedBy,
        existing?.managerLease?.epoch || 1
      ),
    };

    await firestoreMatchRepository.saveSummary(competitionId, summary);
    recordCmoeIo({ writes: 1 });
    if (isCmoeProjectionsEnabled()) {
      await applyMatchSummaryToProjections(firestoreProjectionRepository, competitionId, summary);
    }
    const eventType =
      roomMatch.status === "ready"
        ? "MatchPrepared"
        : roomMatch.status === "draft"
          ? "MatchCreated"
          : "MatchUpdated";
    await recordEvent(competitionId, roomMatch.id, eventType, roomMatch.managedBy, {
      status: roomMatch.status,
    });
    logCmoeSyncInfo("dual_write_setup", { roomCode, matchId: roomMatch.id });
    const result = { ok: true };
    shadowAfter("Setup", {
      roomCode,
      matchId: roomMatch.id,
      roomDoc,
      roomMatches: allRoomMatches,
    });
    return result;
  } catch (err) {
    logCmoeSyncError("dual_write_setup_failed", err, { roomCode, matchId: roomMatch.id });
    return { ok: false, error: err };
  }
  });
}

/**
 * @param {Object} params
 * @param {string} params.roomCode
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch} roomMatch
 * @param {object | null} roomDoc
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} [allRoomMatches]
 * @param {unknown} [scoringMatch] optional live scoring payload
 */
export async function dualWriteRoomMatchGoLive({
  roomCode,
  roomMatch,
  roomDoc,
  allRoomMatches = [],
  scoringMatch = null,
}) {
  if (!isCmoeEnabled() || isCmoePrimaryOrchestration()) return { ok: true, skipped: true };
  const competitionId = resolveCompetitionIdForRoom(roomCode, roomDoc || {});
  const matchId = roomMatch.id;
  const acquiredHoldIds = [];

  try {
    await ensureRoomSessionCompetition(roomCode);
    const parity = checkAllocationParityWithV1(
      allRoomMatches.filter((m) => m.id !== matchId),
      roomMatch.participantEntryIds || []
    );
    if (parity.conflicts.length) {
      logCmoeSyncMismatch("go_live_v1_parity", { roomCode, matchId, conflicts: parity.conflicts });
    }

    const policy = getRoomSessionPolicyBundle();
    const participantIds = roomMatch.participantEntryIds || [];

    if (isCmoeAllocationLedgerEnabled()) {
      for (const pid of participantIds) {
        const holdDocId = buildHoldDocId(competitionId, "participant", pid);
        const existing = await firestoreAllocationRepository.getHold(competitionId, holdDocId);
        const check = canAcquireExclusiveHold(
          existing,
          matchId,
          policy.allocation.allowConcurrentExclusiveParticipation
        );
        if (!check.ok) {
          logCmoeSyncMismatch("allocation_hold_conflict", {
            roomCode,
            matchId,
            participantId: pid,
            competingMatchId: check.competingMatchId,
          });
          return { ok: false, code: "ALLOCATION_CONFLICT" };
        }
      }
      for (const pid of participantIds) {
        const result = await acquireExclusiveHold(
          firestoreAllocationRepository,
          firestoreProjectionRepository,
          {
            competitionId,
            matchId,
            targetType: "participant",
            targetId: pid,
            nowMs: Date.now(),
          }
        );
        if (!result.ok) {
          await rollbackHolds(competitionId, acquiredHoldIds, matchId);
          return { ok: false, code: result.code || "ALLOCATION_FAILED" };
        }
        acquiredHoldIds.push(result.hold.holdId);
      }
      if (roomMatch.courtNumber != null) {
        const courtId = String(roomMatch.courtNumber);
        const courtResult = await acquireExclusiveHold(
          firestoreAllocationRepository,
          firestoreProjectionRepository,
          {
            competitionId,
            matchId,
            targetType: "resource",
            targetId: courtId,
            nowMs: Date.now(),
          }
        );
        if (!courtResult.ok) {
          await rollbackHolds(competitionId, acquiredHoldIds, matchId);
          return { ok: false, code: courtResult.code };
        }
        acquiredHoldIds.push(courtResult.hold.holdId);
      }
    }

    const liveMatch = { ...roomMatch, status: "live" };
    const existing = await firestoreMatchRepository.getSummary(competitionId, matchId);
    let summary = mapRoomMatchToSummary(liveMatch, competitionId);
    summary = {
      ...summary,
      orchestrationState: "active",
      orchestrationVersion: (existing?.orchestrationVersion || 0) + 1,
      managerLease: grantLease(
        existing?.managerLease || summary.managerLease,
        roomMatch.managedBy,
        Date.now(),
        15 * 60 * 1000
      ),
    };
    await firestoreMatchRepository.saveSummary(competitionId, summary);
    recordCmoeIo({ writes: 1 });

    if (isCmoeActivityModuleEnabled()) {
      await persistActivityOnGoLive({
        matchId,
        roomCode,
        summary,
        scoringMatch,
      });
    }

    if (isCmoeProjectionsEnabled()) {
      await applyMatchSummaryToProjections(firestoreProjectionRepository, competitionId, summary);
      if (isCmoeAllocationLedgerEnabled()) {
        await refreshActiveHoldsProjection(
          firestoreProjectionRepository,
          firestoreAllocationRepository,
          competitionId
        );
      }
    }

    await recordEvent(competitionId, matchId, "MatchActivated", roomMatch.managedBy, {});
    logCmoeSyncInfo("dual_write_go_live", { roomCode, matchId });
    shadowAfter("GoLive", {
      roomCode,
      matchId,
      roomDoc,
      roomMatches: allRoomMatches,
    });
    return { ok: true };
  } catch (err) {
    await rollbackHolds(competitionId, acquiredHoldIds, matchId);
    logCmoeSyncError("dual_write_go_live_failed", err, { roomCode, matchId });
    return { ok: false, error: err };
  }
}

async function rollbackHolds(competitionId, holdIds, matchId) {
  if (!holdIds.length) return;
  try {
    await releaseHoldsForMatch(
      firestoreAllocationRepository,
      firestoreProjectionRepository,
      competitionId,
      matchId,
      Date.now()
    );
  } catch (err) {
    logCmoeSyncError("rollback_holds_failed", err, { competitionId, matchId, holdIds });
  }
}

/**
 * @param {Object} params
 * @param {string} params.roomCode
 * @param {string} params.matchId
 * @param {string} params.uid
 * @param {object | null} [roomDoc]
 */
export async function dualWriteRoomMatchCompleted({ roomCode, matchId, uid, roomDoc, roomMatches = [] }) {
  if (!isCmoeEnabled() || isCmoePrimaryOrchestration()) return { ok: true, skipped: true };
  try {
    const competitionId = resolveCompetitionIdForRoom(roomCode, roomDoc || {});
    const existing = await firestoreMatchRepository.getSummary(competitionId, matchId);
    if (!existing) {
      logCmoeSyncMismatch("complete_missing_cmoe_match", { roomCode, matchId });
      return { ok: false, code: "CMOE_MATCH_MISSING" };
    }
    const summary = {
      ...existing,
      orchestrationState: "completed",
      orchestrationVersion: existing.orchestrationVersion + 1,
      bindings: {},
    };
    await firestoreMatchRepository.saveSummary(competitionId, summary);
    recordCmoeIo({ writes: 1 });
    if (isCmoeAllocationLedgerEnabled()) {
      await releaseHoldsForMatch(
        firestoreAllocationRepository,
        firestoreProjectionRepository,
        competitionId,
        matchId,
        Date.now()
      );
    }
    if (isCmoeProjectionsEnabled()) {
      await applyMatchSummaryToProjections(firestoreProjectionRepository, competitionId, summary);
    }
    await recordEvent(competitionId, matchId, ORCHESTRATION_EVENT_TYPES.MATCH_COMPLETED, uid, {});
    logCmoeSyncInfo("dual_write_complete", { roomCode, matchId });
    shadowAfter("Complete", {
      roomCode,
      matchId,
      roomDoc,
      roomMatches,
    });
    return { ok: true };
  } catch (err) {
    logCmoeSyncError("dual_write_complete_failed", err, { roomCode, matchId });
    return { ok: false, error: err };
  }
}

/**
 * @param {Object} params
 * @param {string} params.roomCode
 * @param {string} params.matchId
 * @param {string} params.uid
 * @param {string} params.newHolderUid
 * @param {object | null} [roomDoc]
 */
export async function dualWriteLeaseTransfer({ roomCode, matchId, uid, newHolderUid, roomDoc, roomMatches = [] }) {
  if (!isCmoeEnabled() || isCmoePrimaryOrchestration()) return { ok: true, skipped: true };
  try {
    const competitionId = resolveCompetitionIdForRoom(roomCode, roomDoc || {});
    const existing = await firestoreMatchRepository.getSummary(competitionId, matchId);
    if (!existing) return { ok: false, code: "NOT_FOUND" };
    const lease = grantLease(existing.managerLease, newHolderUid, Date.now(), 15 * 60 * 1000);
    await firestoreMatchRepository.saveSummary(competitionId, {
      ...existing,
      managerLease: lease,
      orchestrationVersion: existing.orchestrationVersion + 1,
    });
    await recordEvent(competitionId, matchId, "LeaseTransferred", uid, {
      from: uid,
      to: newHolderUid,
      epoch: lease.epoch,
    });
    shadowAfter("LeaseTransfer", {
      roomCode,
      matchId,
      roomDoc,
      roomMatches,
    });
    return { ok: true };
  } catch (err) {
    logCmoeSyncError("dual_write_lease_failed", err, { roomCode, matchId });
    return { ok: false, error: err };
  }
}
